import DeviceModel from "../models/DeviceModel";
import { convertToHz } from "../helpers/converters/frequency";
import { convertStringGeoCoordinateToNumber, getDistanceBetweenTwoGeoPoints } from "../helpers/coordinatesHelpers";
import MapModel from "../models/MapModel";
import { getMapScale } from "../helpers/mapHelpers";
import { DeviceTypeEnum } from "../models/enums/DeviceType.enum";
import { CoordinatesFilter } from "../store/filterStore/filterState";
import {
  createPolygonCircle,
  getPolygonIntersection,
} from "../helpers/geometry";

export interface Channel {
  centralFrequency: number,
  bandwidth: number,
}

export function areChannelsOverlap(channel1: Channel, channel2: Channel) {
  // distance between 2 central frequencies is less than half of two bandwidthes
  return Math.abs(channel1.centralFrequency - channel2.centralFrequency) < ((channel1.bandwidth + channel2.bandwidth) / 2)
}

export function filterByChannel(deviceList: DeviceModel[], selectedChannelList: Channel[]) {
  return deviceList.filter((device) => {
    const interfaceList = device.configuration.wirelessInterfaces || [];

    return interfaceList.some((wirelessInterface) => {
      const bandwidth = wirelessInterface.technologyConfiguration?.bandwidth || '';
      const bandwidthInHz = convertToHz(bandwidth);

      return (wirelessInterface.channels || []).some((channel) => {
        return selectedChannelList.some((smartSelectedChannel) => areChannelsOverlap(smartSelectedChannel, {
          bandwidth: bandwidthInHz,
          centralFrequency: channel.centralFrequencyHz,
        }))
      })
    })
  });
}

export function filterByWirelessSystemList(deviceList: DeviceModel[], wirelessSystemIdList: number[]) {
  return deviceList.filter((device) => {
    return device?.configuration?.wirelessSystemId && wirelessSystemIdList.includes(device?.configuration?.wirelessSystemId);
  })
}

export function filterByTechnologyConfigurationList(deviceList: DeviceModel[], technologyConfigurationIdList: number[]) {
  return deviceList.filter((device) => {
    return (device.configuration?.wirelessInterfaces || [])
      .some((wirelessInterface) => technologyConfigurationIdList.includes(wirelessInterface.technologyConfigurationId))
  })
}

export function filterByDistance(deviceList: DeviceModel[], filterCoordinates: CoordinatesFilter, currentMap?: MapModel | null) {
  const radiusInM = parseFloat(filterCoordinates.radius);
  if (!filterCoordinates.latitude || !filterCoordinates.longitude || !radiusInM) {
    return deviceList;
  }

  const mapScale = getMapScale(currentMap);
  const filterDistanceInPx = radiusInM * mapScale;

  return deviceList.filter((device) => {
    if (device?.configuration?.type === DeviceTypeEnum.STATIONARY) {
      const geoPoint = device?.configuration?.mapPoint?.geoPoint;
      if (!geoPoint) {
        return false;
      }
      // @ts-ignore
      const distance = getDistanceBetweenTwoGeoPoints(convertStringGeoCoordinateToNumber(geoPoint), filterCoordinates);
      return distance < radiusInM;
    } else {
      // portable

      const pathString = device?.configuration?.mapPath?.path;

      if (!pathString) {
        return false
      }

      const filterRelativePoint = {
        x: filterCoordinates.x,
        y: filterCoordinates.y,
      };

      const path = JSON.parse(pathString || '[]') as { x: number, y: number }[];

      // @ts-ignore
      const poly = getPolygonIntersection(path, createPolygonCircle(filterRelativePoint, filterDistanceInPx, 90));

      return !!poly.length;
    }
  })
}